<template>
  <div class="video-stream-page anim-fade-in-up">
    <div class="stream-layout">
      <!-- 左侧：视频预览 -->
      <div class="video-panel">
        <div class="video-container">
          <div v-if="!isStreaming" class="video-placeholder">
            <i class="el-icon-video-camera"></i>
            <p>点击右侧"开始接受"按钮开始接收视频流</p>
          </div>
          <div
            v-else
            class="video-mirror-wrap"
            :style="{ transform: `scaleX(${flippedH ? -1 : 1}) scaleY(${flippedV ? -1 : 1})`, filter: `brightness(${brightness}%) contrast(${contrast}%)` }"
          >
            <video
              ref="hlsVideo"
              class="hls-video"
              controls
              playsinline webkit-playsinline x5-playsinline
            ></video>
          </div>
          <!-- 十字准星 -->
          <div v-if="isStreaming" class="crosshair-overlay">
            <div class="ch-line ch-up"></div>
            <div class="ch-line ch-down"></div>
            <div class="ch-line ch-left"></div>
            <div class="ch-line ch-right"></div>
            <span class="ch-center">⊕</span>
          </div>
          <div v-if="isStreaming" class="video-info-overlay">
            <span v-if="connectionStatus !== 'online'">{{ connectionText }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧：参数 & 操作 -->
      <div class="control-panel">
        <!-- 参数设置 -->
        <div class="control-section">
          <span class="small-caps">Picture</span>
          <div class="slider-row">
            <div class="slider-label"><span>亮度</span><b>{{ brightness }}%</b></div>
            <el-slider v-model="brightness" :min="20" :max="200" :step="1" style="padding:0 2px;" />
          </div>
          <div class="slider-row">
            <div class="slider-label"><span>对比度</span><b>{{ contrast }}%</b></div>
            <el-slider v-model="contrast" :min="20" :max="200" :step="1" style="padding:0 2px;" />
          </div>
          <el-button size="mini" @click="brightness=100;contrast=100" style="width:100%; margin-top:4px;">还原默认</el-button>
        </div>

        <hr class="rule-line" />

        <!-- 流控制 -->
        <div class="control-section">
          <span class="small-caps">Stream</span>
          <div class="btn-stack">
            <el-button type="primary" @click="startReceive" :disabled="isStreaming" style="width:100%;">开始接受</el-button>
            <el-button type="danger" @click="stopReceive" :disabled="!isStreaming" style="width:100%;">停止接受</el-button>
          </div>
        </div>

        <hr class="rule-line" />

        <!-- 截屏 -->
        <div class="control-section">
          <span class="small-caps">Capture</span>
          <div class="btn-row">
            <el-button @click="captureScreenshot" :disabled="!isStreaming" style="flex:1;">截屏</el-button>
            <el-button @click="saveScreenshot" :disabled="!lastScreenshot" style="flex:1;">保存</el-button>
          </div>
        </div>

        <hr class="rule-line" />

        <!-- 录制 -->
        <div class="control-section">
          <span class="small-caps">Record</span>
          <div class="btn-row">
            <el-button @click="startRecording" :disabled="!isStreaming || isRecording" style="flex:1;">开始录制</el-button>
            <el-button @click="stopRecording" :disabled="!isRecording" style="flex:1;">停止录制</el-button>
          </div>
          <p v-if="isRecording" class="record-timer"><i class="el-icon-video-camera-solid recording-dot"></i> {{ recordDurationText }}</p>
        </div>

        <hr class="rule-line" />

        <!-- 显示控制 -->
        <div class="control-section">
          <span class="small-caps">Display</span>
          <div class="btn-row">
            <el-button @click="toggleMirrorH" :type="flippedH ? 'warning' : ''" :disabled="!isStreaming" style="flex:1;">
              <i class="el-icon-sort"></i> 水平翻转
            </el-button>
            <el-button @click="toggleMirrorV" :type="flippedV ? 'warning' : ''" :disabled="!isStreaming" style="flex:1;">
              <i class="el-icon-swap"></i> 垂直翻转
            </el-button>
          </div>
          <div class="btn-row" style="margin-top:8px;">
            <el-button @click="toggleFullscreen" :disabled="!isStreaming" style="flex:1;">
              <i class="el-icon-full-screen"></i> 全屏
            </el-button>
            <el-button @click="resetDisplay" :disabled="!isStreaming" style="flex:1;">还原</el-button>
          </div>
        </div>

        <hr class="rule-line" />

        <!-- 状态 -->
        <div class="control-section">
          <span class="small-caps">Status</span>
          <div class="status-list">
            <div class="status-item">
              <span class="status-dot" :class="connectionStatus"></span>
              <span>{{ connectionText }}</span>
            </div>
            <div class="status-item" v-if="isStreaming && videoWidth">
              <span class="status-label">分辨率</span>
              <span>{{ videoWidth }} x {{ videoHeight }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.video-stream-page { max-width: 100%; }

.page-title {
  font-family: var(--font-display);
  font-size: 1.5rem; font-weight: 500;
  color: var(--text-primary); margin: 0 0 24px; letter-spacing: 0.04em;
}

/* ---- 左右分栏 ---- */
.stream-layout {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

/* ---- 左侧：视频 ---- */
.video-panel {
  flex: 1;
  min-width: 0;
  display: flex;
}

.video-container {
  position: relative;
  width: 100%;
  min-height: 480px;
  background: #1A1A1A;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-card);
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-muted);
  gap: 14px;
}
.video-placeholder i { font-size: 52px; opacity: 0.5; }
.video-placeholder p { margin: 0; font-size: 0.88rem; }

.video-mirror-wrap {
  width: 100%; height: 100%;
  display: flex;
}
.hls-video {
  width: 100%; height: 100%;
  display: block; background: #000;
  object-fit: contain;
  cursor: pointer;
}

/* ---- 右侧：控制面板 ---- */
.control-panel {
  width: 320px;
  flex-shrink: 0;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: 20px 22px;
}

.control-section {
  padding: 8px 0;
}
.control-section .small-caps {
  display: block;
  margin-bottom: 12px;
}

.rule-line {
  height: 1px;
  background: var(--border-subtle);
  border: none;
  margin: 4px 0;
}

/* 滑块 */
.slider-row { margin-bottom: 8px; }
.slider-label { display: flex; justify-content: space-between; font-size: 0.78rem; color: var(--text-secondary); margin-bottom: 2px; }
.slider-label b { color: var(--accent-primary); }

/* 参数行 */
.param-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
}

.param-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
  flex-shrink: 0;
  min-width: 42px;
}

.param-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.param-sep {
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: var(--font-mono);
  flex-shrink: 0;
}

/* 按钮组 */
.btn-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-row {
  display: flex;
  gap: 8px;
}

.btn-row .el-button,
.btn-stack .el-button {
  margin: 0;
  font-size: 0.82rem;
}

/* 录制计时 */
.record-timer {
  margin: 10px 0 0;
  font-size: 0.85rem; color: var(--accent-primary);
  font-family: var(--font-mono);
  display: flex; align-items: center; gap: 6px;
}
.recording-dot { animation: blink 1s infinite; color: #e74c3c; }
@keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

/* 状态列表 */
.status-list {
  display: flex; flex-direction: column; gap: 8px;
}
.status-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.82rem; color: var(--text-secondary);
}
.status-label { color: var(--text-muted); min-width: 40px; }
.status-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.status-dot.online { background: #27ae60; box-shadow: 0 0 6px rgba(39,174,96,0.4); }
.status-dot.offline { background: #e74c3c; }
.status-dot.reconnecting { background: #f39c12; animation: blink 0.8s infinite; }

/* 视频信息叠加层 */
/* 十字准星 */
.crosshair-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 6;
}
.ch-center {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  font-size: 22px; color: rgba(255, 255, 255, 0.55); line-height: 1;
  z-index: 1;
}
.ch-line {
  position: absolute;
  background: repeating-linear-gradient(to right, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 4px, transparent 4px, transparent 8px);
}
.ch-up, .ch-down {
  left: 50%; width: 0;
  border-left: 1px dashed rgba(255, 255, 255, 0.3);
}
.ch-up    { bottom: 50%; top: 0; margin-bottom: 16px; }
.ch-down  { top: 50%; bottom: 0; margin-top: 16px; }
.ch-left  { top: 50%; left: 0; right: 50%; margin-right: 16px; height: 0; border-top: 1px dashed rgba(255, 255, 255, 0.3); }
.ch-right { top: 50%; left: 50%; right: 0; margin-left: 16px; height: 0; border-top: 1px dashed rgba(255, 255, 255, 0.3); }

.video-info-overlay {
  position: absolute; top: 10px; left: 10px;
  background: rgba(0,0,0,0.6); color: #fff;
  font-size: 0.75rem; padding: 4px 10px;
  border-radius: var(--radius-sm);
  pointer-events: none;
}

/* ---- 响应式 ---- */
@media screen and (max-width: 860px) {
  .stream-layout {
    flex-direction: column;
  }
  .control-panel {
    width: 100%;
  }
  .video-container {
    max-height: 45vh;
  }
}
</style>

<script>
export default {
  data() {
    return {
      brightness: 100, contrast: 100,
      isStreaming: false, isRecording: false, streamUrl: '', lastScreenshot: null, hlsInstance: null, mediaRecorder: null, recordedChunks: [],
      flippedH: false, flippedV: false, connectionStatus: 'offline',
      videoWidth: 0, videoHeight: 0,
      recordDuration: 0, recordTimer: null,
    }
  },
  computed: {
    connectionText() {
      const map = { online: '已连接', offline: '未连接', reconnecting: '重连中...' };
      return map[this.connectionStatus] || '未知';
    },
    recordDurationText() {
      const d = this.recordDuration;
      const h = Math.floor(d / 3600);
      const m = Math.floor((d % 3600) / 60);
      const s = d % 60;
      if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    },
  },
  beforeDestroy() {
    if (this.hlsInstance) { try { this.hlsInstance.destroy(); } catch (e) {} this.hlsInstance = null; }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') { try { this.mediaRecorder.stop(); } catch (e) {} }
    this.clearRecordTimer();
  },
  methods: {
    async startReceive() {
      try {
        this.$message.info('正在连接视频流...');
        this.connectionStatus = 'reconnecting';
        const resp = await this.$axios.get('/api/video/stream/url');
        if (resp.data.code !== 0) { this.$message.error('获取视频流地址失败: ' + resp.data.msg); this.connectionStatus = 'offline'; return; }
        this.streamUrl = resp.data.data.streamUrl; this.isStreaming = true;
        this.$nextTick(() => {
          const videoEl = this.$refs.hlsVideo;
          if (!videoEl) { this.$message.error('播放器初始化失败'); this.isStreaming = false; this.connectionStatus = 'offline'; return; }
          if (this.hlsInstance) { try { this.hlsInstance.destroy(); } catch (e) {} this.hlsInstance = null; }
          if (window.Hls && window.Hls.isSupported()) {
            videoEl.removeAttribute('src'); videoEl.load();
            const hls = new window.Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 30, maxBufferLength: 6, maxMaxBufferLength: 12, liveSyncDurationCount: 999, liveMaxLatencyDurationCount: 1000, debug: false });
            hls.loadSource(this.streamUrl); hls.attachMedia(videoEl);
            hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
              this.$message.success('视频流已加载，请点击播放');
              this.connectionStatus = 'online';
              this.videoWidth = videoEl.videoWidth || 0;
              this.videoHeight = videoEl.videoHeight || 0;
            });
            hls.on(window.Hls.Events.ERROR, (evt, data) => {
              if (data.fatal) {
                this.connectionStatus = 'reconnecting';
                if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
                else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
                else { this.$message.error('视频加载失败'); this.connectionStatus = 'offline'; }
              }
            });
            this.hlsInstance = hls;
          } else {
            videoEl.src = this.streamUrl;
            videoEl.onloadedmetadata = () => {
              this.connectionStatus = 'online';
              this.videoWidth = videoEl.videoWidth || 0;
              this.videoHeight = videoEl.videoHeight || 0;
            };
            videoEl.load();
            this.$message.success('视频流已加载');
          }
        });
      } catch (e) { this.$message.error('启动视频流失败'); this.isStreaming = false; this.connectionStatus = 'offline'; }
    },
    stopReceive() {
      if (this.hlsInstance) { try { this.hlsInstance.destroy(); } catch (e) {} this.hlsInstance = null; }
      const videoEl = this.$refs.hlsVideo; if (videoEl) { try { videoEl.pause(); } catch (e) {} videoEl.removeAttribute('src'); videoEl.load(); }
      this.isStreaming = false; this.streamUrl = ''; this.connectionStatus = 'offline';
      this.videoWidth = 0; this.videoHeight = 0;
      this.clearRecordTimer();
      this.$message.info('已停止视频流');
    },
    captureScreenshot() {
      if (!this.isStreaming) { this.$message.warning('请先开始接收视频流'); return; }
      const video = this.$refs.hlsVideo; if (!video || video.readyState < 2) { this.$message.error('视频未就绪'); return; }
      try {
        const canvas = document.createElement('canvas'); canvas.width = video.videoWidth || 1920; canvas.height = video.videoHeight || 1080;
        const ctx = canvas.getContext('2d');
        ctx.filter = `brightness(${this.brightness}%) contrast(${this.contrast}%)`;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => { this.lastScreenshot = blob; this.$message.success('截屏成功'); }, 'image/png');
      } catch (e) { this.$message.error('截屏失败'); }
    },
    saveScreenshot() {
      if (!this.lastScreenshot) { this.$message.warning('没有可保存的截屏'); return; }
      const url = URL.createObjectURL(this.lastScreenshot); const a = document.createElement('a'); a.href = url; a.download = `screenshot_${Date.now()}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); this.$message.success('截屏已保存');
    },
    startRecording() {
      if (!this.isStreaming) { this.$message.warning('请先开始接收视频流'); return; }
      const video = this.$refs.hlsVideo; if (!video) { this.$message.error('未找到视频元素'); return; }
      if (typeof MediaRecorder === 'undefined') { this.$message.error('浏览器不支持录制'); return; }
      try {
        const vw = video.videoWidth || 640, vh = video.videoHeight || 480;
        const canvas = document.createElement('canvas'); canvas.width = vw; canvas.height = vh;
        const ctx = canvas.getContext('2d');
        // 手动帧率模式：captureStream(0) + requestFrame
        const stream = canvas.captureStream(0);
        const track = stream.getVideoTracks()[0];
        this.recordedChunks = [];
        const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8', videoBitsPerSecond: 2000000 });
        recorder.ondataavailable = e => { if (e.data && e.data.size > 0) this.recordedChunks.push(e.data); };
        recorder.onstop = () => {
          if (this._rafId) cancelAnimationFrame(this._rafId);
          if (!this.recordedChunks.length) { this.$message.warning('没有录制到数据'); return; }
          const mime = 'video/webm';
          const blob = new Blob(this.recordedChunks, { type: mime }); const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `record_${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
          this.$message.success('录制完成'); this.isRecording = false; this.mediaRecorder = null; this.recordedChunks = [];
        };
        recorder.onerror = () => { this.$message.error('录制出错'); this.isRecording = false; this.mediaRecorder = null; this.recordedChunks = []; };
        // RAF 绘制帧并手动推送到流
        const fps = 15; let lastTime = 0;
        const draw = (ts) => {
          if (!this.isRecording) return;
          if (ts - lastTime >= 1000 / fps) {
            ctx.filter = `brightness(${this.brightness}%) contrast(${this.contrast}%)`;
            ctx.drawImage(video, 0, 0, vw, vh);
            try { track.requestFrame(); } catch(e) {}
            lastTime = ts;
          }
          this._rafId = requestAnimationFrame(draw);
        };
        this._rafId = requestAnimationFrame(draw);
        recorder.start(500); this.mediaRecorder = recorder; this.isRecording = true;
        this.recordDuration = 0; this.startRecordTimer();
        this.$message.success('开始录制');
      } catch (e) { console.error(e); this.$message.error('录制失败，请尝试关闭Chrome硬件加速或换Edge浏览器'); }
    },
    stopRecording() {
      if (!this.isRecording || !this.mediaRecorder) { this.$message.warning('当前没有录制'); return; }
      if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
      try { if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop(); } catch (e) { this.$message.error('停止录制失败'); }
      this.clearRecordTimer(); this.recordDuration = 0;
    },
    startRecordTimer() { this.clearRecordTimer(); this.recordTimer = setInterval(() => { this.recordDuration++; }, 1000); },
    clearRecordTimer() { if (this.recordTimer) { clearInterval(this.recordTimer); this.recordTimer = null; } },
    toggleFullscreen() {
      const el = document.querySelector('.video-container');
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen().catch(() => {});
      }
    },
    toggleMirrorH() { this.flippedH = !this.flippedH; },
    toggleMirrorV() { this.flippedV = !this.flippedV; },
    resetDisplay() { this.flippedH = false; this.flippedV = false; },
  }
}
</script>
